/*!
 * Dr.Golf Swing Assessment — CONTENT LIBRARY
 * ------------------------------------------------------------------
 * Diagnoses, secondary findings, free advice, practice protocols and
 * product copy. Rules are evaluated IN ORDER; the first match wins.
 *
 * Condition keys (all optional, all must pass):
 *   all:     ['q4:B', …]          every listed answer was selected
 *   any:     ['q7:B', …]          at least one was selected
 *   none:    ['q3:B', …]          none were selected
 *   min:     { of: [...], n: 2 }   at least n of the list were selected (or an array of these)
 *   dims:    { P: { gte: 40 } }    normalized dimension score (0–100)
 *   support: { T: { gte: 2 } }    number of answers supporting a family dimension
 * A `when` that is an ARRAY means "any one of these condition sets".
 *
 * `evidenceFrom` lists answers whose evidence statements are preferred on the
 * results page. Statements are only ever shown for answers the golfer chose.
 * ------------------------------------------------------------------ */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else { root.DrGolf = root.DrGolf || {}; root.DrGolf.content = factory(); }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ================= PRIMARY DIAGNOSES ================= */
  var diagnoses = {
    tempo: [
      {
        id: 'pressure-accelerator', name: 'The Pressure Accelerator', short: 'Pressure Accelerator',
        when: { all: ['q4:B', 'q10:B'], none: ['q3:B'] },
        evidenceFrom: ['q4:B', 'q10:B', 'q3:E', 'q6:A', 'q6:C'],
        summary: 'Your answers suggest your rhythm holds up in practice, then speeds up once the score matters—on the course and on shots with trouble around them.',
        meaning: [
          'When a shot feels important, many golfers speed up the backswing or transition without deciding to. The swing itself hasn’t changed much; the pace it runs at has.',
          'Training a rhythm you can recognize—and rehearse before the shots that matter—gives you something familiar to return to when the pace starts to creep up.'
        ],
        problemLine: 'Your rhythm speeds up when the shot matters.',
        protocol: 'transition-rhythm'
      },
      {
        id: 'rushed-transition', name: 'The Rushed Transition', short: 'Rushed Transition',
        when: { min: { of: ['q3:B', 'q7:B', 'q10:B'], n: 2 } },
        evidenceFrom: ['q3:B', 'q7:B', 'q10:B', 'q1:B', 'q4:B', 'q6:A', 'q6:C'],
        summary: 'Your answers suggest your transition becomes quicker when you add effort or feel pressure. That can make it harder to rehearse a repeatable rhythm.',
        meaning: [
          'The transition—the moment your backswing turns into your downswing—is where many swings speed up without the golfer choosing to. A hurried start down leaves less time for the body and club to sequence, which often shows up as inconsistent timing and strike.',
          'The goal isn’t to swing slowly. It’s to find a pace you can repeat, so the change of direction feels deliberate rather than hurried.'
        ],
        problemLine: 'Your transition gets quick from the top.',
        protocol: 'transition-rhythm'
      },
      {
        id: 'power-at-wrong-time', name: 'Power at the Wrong Time', short: 'Power at the Wrong Time',
        when: {
          dims: { E: { gte: 40 } }, any: ['q1:E', 'q7:E', 'q10:E'],
          min: { of: ['q1:E', 'q2:E', 'q7:B', 'q7:E', 'q8:B', 'q8:C', 'q10:E'], n: 3 }
        },
        evidenceFrom: ['q1:E', 'q7:E', 'q7:B', 'q2:E', 'q8:C', 'q8:B', 'q10:E', 'q6:C', 'q6:A'],
        summary: 'Your answers suggest that when you go looking for extra distance, the effort shows up early in the swing—and contact pays for it before the distance arrives.',
        meaning: [
          'Speed is most useful when it’s delivered through the bottom of the swing. When golfers try to add it, the extra effort often shows up as a quicker start down instead, which can cost strike quality.',
          'Training a rhythm that stays the same at 80% and at full effort is a practical way to find out how much speed you can add before the swing loses its timing.'
        ],
        problemLine: 'Extra effort shows up too early in your swing.',
        protocol: 'controlled-effort'
      },
      {
        id: 'rhythm-under-pressure', name: 'Rhythm Breakdown Under Pressure', short: 'Rhythm Breakdown Under Pressure',
        when: { dims: { P: { gte: 40 } }, min: { of: ['q3:E', 'q4:B', 'q4:E', 'q10:B', 'q8:D'], n: 2 } },
        evidenceFrom: ['q3:E', 'q4:B', 'q4:E', 'q10:B', 'q8:D', 'q6:A'],
        summary: 'Your answers suggest your timing gets less predictable when the situation changes—on the course, under pressure, or when a shot matters.',
        meaning: [
          'A swing that works on the range but changes on the course is rarely a mystery of mechanics. More often, the pace of the swing changes with the situation, and the rest of the motion has to adapt on the fly.',
          'A rhythm you’ve rehearsed enough to recognize gives you a reference point to come back to, whatever the situation.'
        ],
        problemLine: 'Your timing changes when the situation does.',
        protocol: 'transition-rhythm'
      },
      {
        id: 'tempo-overthinking', name: 'Tempo Overthinking', short: 'Tempo Overthinking',
        when: { dims: { O: { gte: 60 } }, support: { T: { gte: 2 } } },
        evidenceFrom: ['q9:C', 'q9:E', 'q9:D', 'q3:B', 'q1:B', 'q2:D', 'q6:E'],
        summary: 'Your answers suggest a timing issue that’s competing with a busy mind over the ball. When several swing thoughts are active at once, rhythm is often the first thing to go.',
        meaning: [
          'Trying to control several positions at once tends to make a swing more stop-start. Rhythm is a single, simple thing to pay attention to—which makes it a good replacement for a list of mechanical checkpoints.',
          'Practicing to a sound or a count gives your attention one job, so the rest of the swing has room to flow.'
        ],
        problemLine: 'Too many swing thoughts are crowding out your rhythm.',
        protocol: 'tempo-intro'
      },
      {
        id: 'effort-rhythm-breakdown', name: 'Effort-Driven Rhythm Breakdown', short: 'Effort-Driven Rhythm Breakdown',
        when: {
          any: ['q7:B', 'q7:E', 'q2:E', 'q1:E'],
          min: { of: ['q3:B', 'q1:B', 'q2:D', 'q3:E', 'q4:B', 'q10:B', 'q6:A', 'q6:C'], n: 1 }
        },
        evidenceFrom: ['q7:B', 'q7:E', 'q2:E', 'q1:E', 'q3:B', 'q6:A', 'q6:C', 'q1:B'],
        summary: 'Your answers suggest your rhythm is most likely to break down when you add speed or effort.',
        meaning: [
          'Many golfers have a perfectly good rhythm at normal effort. Adding speed changes the pace of the swing, and the timing that worked a moment ago no longer lines up.',
          'Practicing the same rhythm at gradually increasing effort is a practical way to find the level where your swing still feels like yours.'
        ],
        problemLine: 'Your rhythm breaks down when you add effort.',
        protocol: 'controlled-effort'
      },
      {
        id: 'inconsistent-tempo', name: 'Inconsistent Tempo', short: 'Inconsistent Tempo',
        when: { any: ['q1:B', 'q2:D', 'q3:E'], min: { of: ['q1:B', 'q1:D', 'q2:C', 'q2:D', 'q3:E'], n: 2 } },
        evidenceFrom: ['q1:B', 'q2:D', 'q3:E', 'q1:D', 'q2:C', 'q6:A'],
        summary: 'Your answers suggest your swing’s timing varies from one swing to the next—good when it lines up, hard to repeat when it doesn’t.',
        meaning: [
          'When tempo changes from swing to swing, everything that depends on it—strike, direction, distance—changes too. That can feel like a different problem every day, even when it has a common thread.',
          'A consistent rhythm doesn’t need to be slow. It needs to be familiar enough that you can find it again.'
        ],
        problemLine: 'Your swing’s timing changes from one swing to the next.',
        protocol: 'tempo-intro'
      }
    ],
    connection: [
      {
        id: 'body-stall', name: 'Body Stall, Arms Take Over', short: 'Body Stall / Arms Take Over',
        when: { all: ['q5:C'], any: ['q1:C', 'q3:C', 'q7:C', 'q2:A', 'q2:B', 'q7:D'] },
        evidenceFrom: ['q5:C', 'q1:C', 'q3:C', 'q7:C', 'q2:B', 'q2:A', 'q6:B'],
        summary: 'Your answers suggest that your body can slow or stop turning while your hands and arms keep going and take over the job.',
        meaning: [
          'In a coordinated swing, the arms and body keep moving together through the hitting area. If the turn slows down, the hands and arms often carry on alone—which can make strike and direction harder to repeat.',
          'Rehearsing slow swings where your chest and arms finish turning together is a practical way to feel the difference.'
        ],
        problemLine: 'Your body stops turning and your arms take over.',
        protocol: 'connection-half-swing'
      },
      {
        id: 'arms-first', name: 'The Arms-First Pattern', short: 'Arms-First Pattern',
        when: { any: ['q3:C', 'q7:C'], min: { of: ['q3:C', 'q7:C', 'q1:C'], n: 2 } },
        evidenceFrom: ['q3:C', 'q7:C', 'q1:C', 'q5:B', 'q6:B'],
        summary: 'Your answers suggest your arms and hands tend to lead the swing instead of moving with your body’s turn.',
        meaning: [
          'When the arms start the downswing on their own, the body has to play catch-up—or doesn’t get involved at all. Golfers often describe it as the hands “taking over.”',
          'Training the feeling of your upper arms staying with your chest gives the body a chance to lead the motion, with the arms along for the ride.'
        ],
        problemLine: 'Your arms fire before your body.',
        protocol: 'connection-intro'
      },
      {
        id: 'body-outruns-arms', name: 'Body Outruns the Arms', short: 'Body-Outruns-Arms Pattern',
        when: { any: ['q3:D', 'q5:D'], min: { of: ['q3:D', 'q5:D', 'q7:D', 'q6:B'], n: 2 } },
        evidenceFrom: ['q3:D', 'q5:D', 'q7:D', 'q6:B'],
        summary: 'Your answers suggest your body turn can get ahead of your arms, leaving the arms working to catch up.',
        meaning: [
          'Turning hard isn’t a problem by itself. The challenge is when the arms don’t travel with the turn, so they arrive late and have to rush to square up.',
          'Practicing slow swings with your arms and chest moving at the same pace helps you feel what “together” is like before you add speed.'
        ],
        problemLine: 'Your body turns and your arms get left behind.',
        protocol: 'connection-half-swing'
      },
      {
        id: 'arm-body-disconnect', name: 'Arm–Body Disconnect', short: 'Arm–Body Disconnect',
        when: { any: ['q1:C', 'q5:B'], min: { of: ['q1:C', 'q5:B', 'q6:B', 'q2:B', 'q7:C', 'q3:C'], n: 2 } },
        evidenceFrom: ['q5:B', 'q1:C', 'q6:B', 'q7:C', 'q3:C', 'q2:B'],
        summary: 'Your answers suggest your arms and body don’t always move as one unit, with your arms sometimes working independently of your turn.',
        meaning: [
          'When the arms work independently, each swing depends on timing them back up with the body. Some days that works beautifully. Other days it doesn’t.',
          'A physical cue that keeps your upper arms connected to your chest makes “moving together” something you can feel, not just think about.'
        ],
        problemLine: 'Your arms and body don’t always move together.',
        protocol: 'connection-intro'
      },
      {
        id: 'structure-loss-effort', name: 'Structure Loss Under Effort', short: 'Structure Loss Under Effort',
        when: { all: ['q7:D'], support: { C: { gte: 2 } } },
        evidenceFrom: ['q7:D', 'q5:B', 'q5:C', 'q5:D', 'q3:C', 'q3:D', 'q1:C', 'q6:B', 'q6:D'],
        summary: 'Your answers suggest that when you add effort, your swing loses balance and shape—alongside other answers pointing to your arms and body drifting apart.',
        meaning: [
          'Adding effort tends to expose whatever isn’t quite connected. If the arms and body aren’t working as a unit, extra speed pulls them further apart and the swing loses its shape.',
          'Building connection at slow speed first, then adding effort gradually, gives the structure a chance to hold.'
        ],
        problemLine: 'Your swing loses its shape when you add effort.',
        protocol: 'connection-half-swing'
      },
      {
        id: 'connection-overthinking', name: 'Connection Overthinking', short: 'Connection Overthinking',
        when: { dims: { O: { gte: 60 } }, support: { C: { gte: 2 } } },
        evidenceFrom: ['q9:C', 'q9:E', 'q9:D', 'q5:B', 'q5:C', 'q5:D', 'q1:C', 'q3:C'],
        summary: 'Your answers suggest arm–body coordination issues alongside a busy mind over the ball—trying to control several positions with the arms at once.',
        meaning: [
          'Trying to place the arms in specific positions often makes them more independent, not less. Coordination tends to improve when it’s felt rather than steered.',
          'A physical cue does some of the thinking for you, so your attention can stay on one simple feeling.'
        ],
        problemLine: 'You’re thinking about your arms instead of feeling them.',
        protocol: 'connection-intro'
      },
      {
        id: 'connection-consistency', name: 'Connection Consistency Problem', short: 'Connection Consistency',
        when: { any: ['q1:A', 'q1:D', 'q2:A', 'q2:B'], support: { C: { gte: 2 } } },
        evidenceFrom: ['q1:A', 'q2:A', 'q2:B', 'q1:D', 'q5:B', 'q5:C', 'q5:D', 'q6:B'],
        summary: 'Your answers suggest inconsistent results alongside repeated signs that your arms and body don’t always work together.',
        meaning: [
          'When the arms and body are only sometimes in sync, the result is a swing that’s great one moment and unfamiliar the next.',
          'Rehearsing the connected feeling at slow speed makes it easier to recognize—and to find again.'
        ],
        problemLine: 'Your connection comes and goes, and so does your strike.',
        protocol: 'connection-intro'
      }
    ]
  };

  /* Broad labels used when no specific pattern is supported. */
  var fallbacks = {
    tempo: {
      id: 'tempo-focus', name: 'Tempo & Rhythm Focus', short: 'Tempo & Rhythm Focus',
      summary: 'Your answers point toward rhythm and timing as the most useful place to start training.',
      meaning: [
        'Rhythm ties the rest of the swing together. When it’s consistent, the other parts have a better chance of lining up.',
        'Starting with a repeatable pace is a low-risk, high-value place to begin—especially if your best swings feel smooth.'
      ],
      problemLine: 'A repeatable rhythm is your best place to start.',
      protocol: 'tempo-intro'
    },
    connection: {
      id: 'connection-focus', name: 'Arm–Body Connection Focus', short: 'Arm–Body Connection Focus',
      summary: 'Your answers point toward arm–body coordination as the most useful place to start training.',
      meaning: [
        'When the arms and body move together, the swing tends to be easier to repeat.',
        'Slow, connected practice swings are a simple, low-risk way to build that feeling.'
      ],
      problemLine: 'Moving your arms and body together is your best place to start.',
      protocol: 'connection-intro'
    }
  };

  var combined = {
    id: 'rhythm-connection', name: 'The Rhythm + Connection Pattern', short: 'Rhythm + Connection Pattern',
    summary: 'Your answers point to two related training priorities: the pace of your swing, and how well your arms and body move together. Neither stands out clearly over the other.',
    meaning: [
      'Tempo and connection affect each other. A rushed swing makes it harder for the arms and body to stay together, and a disconnected swing makes rhythm harder to find.',
      'Practicing each feeling on its own first—then bringing them together in your warm-up—keeps each session simple.'
    ],
    problemLine: 'Your rhythm and your connection both come and go.',
    protocol: 'combined'
  };

  var starter = {
    tempo: {
      id: 'starter-tempo', name: 'Starter Focus: Rhythm', short: 'Starter Focus: Rhythm',
      summary: 'Your answers don’t point strongly to one specific pattern, so we’re not going to invent one. A repeatable rhythm is a sensible place to start—and it’s easy to switch focus later.',
      pressure: 'Pressure is the clearest theme in your answers; the mechanical signals are limited. A simple rhythm you can rely on when a shot matters is a sensible place to start.',
      meaning: [
        'Rhythm is the most general-purpose thing to practice: it supports every other part of the swing and doesn’t require you to diagnose a specific fault first.'
      ],
      problemLine: 'A repeatable rhythm is a sensible place to start.',
      protocol: 'tempo-intro'
    },
    connection: {
      id: 'starter-connection', name: 'Starter Focus: Connection', short: 'Starter Focus: Connection',
      summary: 'Your answers don’t point strongly to one specific pattern, so we’re not going to invent one. Arm–body connection is a sensible place to start—and it’s easy to switch focus later.',
      pressure: 'Pressure is the clearest theme in your answers; the mechanical signals are limited. A slow, connected swing you can rehearse before shots that matter is a sensible place to start.',
      meaning: [
        'Connection practice is simple, slow and low-risk. It gives you a clear feeling to rehearse without needing a specific fault to fix.'
      ],
      problemLine: 'Arms and body moving together is a sensible place to start.',
      protocol: 'connection-intro'
    }
  };

  var maintenance = {
    id: 'maintenance', name: 'A Swing Worth Maintaining', short: 'Maintenance Focus',
    summary: 'You reported very little breaking down under pressure, effort or competition. That’s genuinely good news—and we’re not going to invent a problem. The useful goal is keeping the rhythm and connection you already have repeatable.',
    meaning: [
      'Golfers with a stable swing still benefit from a short, consistent warm-up. It’s the easiest way to make sure the swing you bring to the first tee is the one you practiced.'
    ],
    problemLine: 'Keep the swing you have repeatable.',
    protocol: 'maintenance'
  };

  /* Shown under the reveal. No percentages, no probabilities. */
  var strengthNotes = {
    strong: { label: 'Consistent across your answers', text: 'Several of your answers—including technical ones—point the same way.' },
    moderate: { label: 'Supported by a few answers', text: 'A few of your answers point this way. Treat it as a sensible place to start, not a verdict.' },
    tentative: { label: 'Early indication', text: 'Your answers don’t point strongly in one direction, so this is a starting focus rather than a firm pattern.' }
  };

  /* ================= SECONDARY FINDINGS (the lighter side) ================= */
  // group: only the strongest finding in a group is shown.
  var secondary = [
    {
      id: 'foursome-ego', name: 'Foursome Ego Syndrome', group: 'effort', dim: 'E', advice: 'competitive',
      when: [{ any: ['q8:C'] }, { all: ['q8:B'], dims: { E: { gte: 50 } } }],
      signals: ['q8:B', 'q8:C', 'q7:E', 'q10:E', 'q1:E'],
      text: 'Your swing apparently develops a sudden horsepower upgrade whenever someone outdrives you. Unfortunately, the extra effort doesn’t always come with extra distance.',
      levelText: '{level} competitive over-swing tendency. Particularly noticeable after someone in your group hits a bomb.'
    },
    {
      id: 'unbothered', name: 'Totally-Doesn’t-Care Syndrome', group: 'effort', dim: 'E', advice: 'competitive',
      when: { all: ['q8:E'] },
      signals: ['q8:E', 'q7:E', 'q10:E'],
      text: 'You don’t care who outdrives you. Not even slightly. Your next tee shot may have a different opinion.',
      levelText: 'Self-reported. Confirmed by your own answer.'
    },
    {
      id: 'distance-chasing', name: 'Distance-Chasing Syndrome', group: 'effort', dim: 'E', advice: 'competitive',
      when: [{ any: ['q7:E'] }, { any: ['q10:E'] }, { all: ['q1:E'], dims: { E: { gte: 30 } } }],
      signals: ['q7:E', 'q10:E', 'q1:E', 'q2:E'],
      text: 'Your next drive doesn’t need to avenge the previous one.',
      levelText: '{level} tendency to add effort in search of distance.'
    },
    {
      id: 'grip-tension', name: 'Grip-Tension Habit', group: 'tension', dim: 'P', advice: 'grip',
      when: { any: ['q4:C', 'q10:C'] },
      signals: ['q4:C', 'q10:C', 'q8:D'],
      text: 'Your golf club isn’t trying to escape. You can loosen your hands a little.',
      levelText: 'Shows up in your hands and arms when it counts.'
    },
    {
      id: 'first-tee-tension', name: 'First-Tee Tension', group: 'tension', dim: 'P', advice: 'pressure',
      when: [{ all: ['q8:D'] }, { dims: { P: { gte: 70 } } }],
      signals: ['q8:D', 'q4:B', 'q10:B', 'q3:E'],
      text: 'Your swing knows what to do. It just becomes a little more dramatic when people are watching.',
      levelText: '{level} pressure sensitivity.'
    },
    {
      id: 'pressure-steering', name: 'Pressure Steering', group: 'steering', dim: 'P', advice: 'steering',
      when: { any: ['q4:D', 'q10:D'] },
      signals: ['q4:D', 'q10:D'],
      text: 'When the fairway narrows, you start trying to personally escort the ball to safety.',
      levelText: 'Appears when a shot feels like it matters.'
    },
    {
      id: 'range-to-course', name: 'Range-to-Course Disappearing Act', group: 'range', dim: 'P', advice: 'range-course',
      when: { any: ['q4:E'] },
      signals: ['q4:E', 'q3:E'],
      text: 'That beautiful range swing sometimes refuses to clock in for your actual round.',
      levelText: 'Intermittent. Usually reported on days that count.'
    },
    {
      id: 'youtube-overload', name: 'YouTube Swing-Thought Overload', group: 'mental', dim: 'O', advice: 'overthinking',
      when: { any: ['q9:C', 'q9:E'] },
      signals: ['q9:C', 'q9:E', 'q6:E'],
      text: 'Your swing currently has more instructions than your washing machine.',
      levelText: '{level} mental load over the ball.'
    },
    {
      id: 'last-shot-revenge', name: 'Last-Shot Revenge Syndrome', group: 'mental', dim: 'O', advice: 'revenge',
      when: { any: ['q9:D'] },
      signals: ['q9:D', 'q6:E'],
      text: 'One bad shot, three new swing theories. Not the most relaxing system.',
      levelText: 'Triggered by your most recent bad shot.'
    }
  ];

  var levelWords = { low: 'Mild', moderate: 'Moderate', high: 'Strong' };

  /* ================= FREE (NON-COMMERCIAL) ADVICE ================= */
  var advice = {
    tempo: {
      title: 'Practice one repeatable rhythm',
      lead: 'Rhythm is free to practice. You don’t need anything from us to start.',
      steps: [
        'Hit 10 balls at what feels like 70% effort.',
        'Say a steady “one… two” in your head—“one” to the top, “two” through the ball.',
        'Keep the count identical every swing. You’re training the same pace, not maximum speed.'
      ]
    },
    connection: {
      title: 'Slow, connected half-swings',
      lead: 'Connection is easiest to feel at low speed.',
      steps: [
        'Make 10 slow half-swings—hip height to hip height—within a range that feels comfortable.',
        'Let your chest and arms arrive at the finish together.',
        'If your arms get there first, slow down until they don’t.'
      ]
    },
    pressure: {
      title: 'A pre-shot routine you can repeat',
      lead: 'We don’t sell a fix for first-tee nerves. Start with a consistent pre-shot breathing routine.',
      steps: [
        'Pick a specific, small target—not just “the fairway.”',
        'Take one slow exhale before you step in to the ball.',
        'Use the same timing and the same number of waggles every shot, on the range and on the course.'
      ]
    },
    overthinking: {
      title: 'One swing thought per session',
      lead: 'Fewer instructions, better swings.',
      steps: [
        'Choose a single focus before you practice—one feeling, not a checklist.',
        'Keep it for the entire session, even after a bad shot.',
        'On the course, let your only thought over the ball be the target.'
      ]
    },
    competitive: {
      title: 'Compare 80% and 100% swings',
      lead: 'Find out what extra effort actually buys you.',
      steps: [
        'On the range, hit 5 balls at a comfortable 80% and 5 at full effort.',
        'Compare strike quality and carry distance, not how hard it felt.',
        'Many golfers find the distance gap is smaller than expected—and the strike gap bigger.'
      ]
    },
    grip: {
      title: 'Check your grip pressure',
      lead: 'Firm enough to control the club. Not tighter.',
      steps: [
        'On a 1–10 scale, try holding the club at about a 4 and hit a few balls.',
        'Notice whether your forearms stay relaxed through the swing.',
        'Before shots that matter, check your grip pressure as part of your routine.'
      ]
    },
    steering: {
      title: 'Commit to a target, then let it go',
      lead: 'Steering usually comes from trying to control the outcome mid-swing.',
      steps: [
        'On the range, pick two targets to form a “fairway” and hit to it.',
        'Hold a full, balanced finish on every shot, wherever the ball goes.',
        'Grade yourself on commitment to the finish, not on result.'
      ]
    },
    'range-course': {
      title: 'Practice like you play',
      lead: 'Make your range session look more like a round.',
      steps: [
        'Change club and target every shot instead of hitting the same 7-iron 20 times.',
        'Use your full pre-shot routine on each ball.',
        'Finish each session by “playing” the first three holes of your course on the range.'
      ]
    },
    revenge: {
      title: 'A reset routine after a bad shot',
      lead: 'The next shot doesn’t need a new theory.',
      steps: [
        'After a bad shot, take one deliberate breath and name the target for the next shot.',
        'Keep the same swing thought for the rest of the hole—no mid-round rebuilds.',
        'Save swing changes for your next practice session.'
      ]
    },
    maintenance: {
      title: 'A five-minute warm-up you repeat every time',
      lead: 'Consistency starts before the first tee.',
      steps: [
        '10 slow rehearsal swings at a steady count.',
        '5 half-swings feeling your arms and chest move together.',
        'A few shots with your opening club, using your full routine.'
      ]
    }
  };

  /* ================= PRACTICE PROTOCOLS ================= */
  // VERIFY with the physical product: where the Tempo Swing Stick click should ideally occur.
  var protocols = {
    'tempo-intro': {
      title: 'Introductory Tempo Routine', product: 'tempo', duration: '10 minutes · 3–4 times a week',
      setup: 'Hold the Tempo Swing Stick using the molded training grip as your guide for hand placement. Take your normal stance with plenty of clear space around you.',
      steps: [
        { t: 'Listen first', d: '5 slow half-swings. Notice where in the swing you hear the click. Don’t try to change anything yet.' },
        { t: 'Find a repeatable click', d: '10 full rehearsal swings at a comfortable pace. Aim for the click to arrive at the same point every time—ideally through the bottom of the swing rather than early in the downswing.' },
        { t: 'Add a count', d: '5 swings saying “one” to the top and “two” through the click.' },
        { t: 'Take it to the ball', d: 'Hit 5 balls with a mid-iron using the same count.' }
      ],
      feel: 'A swing that changes direction without hurrying, with the click arriving at the same point each rep.',
      mistakes: ['Swinging harder just to make it click.', 'Changing your count from swing to swing.', 'Practicing without enough clear space.'],
      progress: 'Once the click feels consistent at 70% effort for a week, add a few swings at 80%—same count.'
    },
    'transition-rhythm': {
      title: 'Transition-Rhythm Routine', product: 'tempo', duration: '10–12 minutes · 3–4 times a week',
      setup: 'Hold the Tempo Swing Stick with the molded grip as your hand-placement guide. Make sure you have clear space all around.',
      steps: [
        { t: 'Pause at the top', d: '5 swings where you pause for a beat at the top, then start down smoothly. Notice where the click arrives.' },
        { t: 'Shorten the pause', d: '5 swings with a shorter pause, keeping the same smooth start down.' },
        { t: 'Remove the pause', d: '5 continuous swings that keep the same unhurried change of direction—listen for a consistent click.' },
        { t: 'Pressure rep', d: 'Before each of your last 3 swings, run your pre-shot routine as if it were the first tee.' }
      ],
      feel: 'A change of direction that feels deliberate—not slow, just not rushed.',
      mistakes: ['Swinging slowly instead of smoothly.', 'Rushing the start down to “get” the click.', 'Skipping the routine on the pressure reps.'],
      progress: 'Bring a few pressure reps into every warm-up before a round.'
    },
    'controlled-effort': {
      title: 'Controlled-Effort Routine', product: 'tempo', duration: '10 minutes · 3 times a week',
      setup: 'Hold the Tempo Swing Stick using the molded grip as your guide. Clear space all around.',
      steps: [
        { t: '60% swings', d: '5 rehearsal swings at 60% effort. Note where the click happens.' },
        { t: '80% swings', d: '5 swings at 80%. The click should arrive at the same point—if it moves earlier, you’ve rushed.' },
        { t: '100% swings', d: '3 swings at full effort, keeping the same rhythm. If the click moves, go back to 80%.' },
        { t: 'On the range', d: 'Hit 5 balls at 80% and 5 at 100%. Compare strike, not feel.' }
      ],
      feel: 'Speed that builds through the swing rather than arriving all at once from the top.',
      mistakes: ['Jumping straight to full effort.', 'Judging success by how hard it felt.', 'Letting the count speed up with the effort.'],
      progress: 'Increase effort only when the click stays consistent at the previous level.'
    },
    'connection-intro': {
      title: 'Introductory Connection Routine', product: 'band', duration: '10 minutes · 3–4 times a week',
      setup: 'Place the Connection Band around both upper arms, just above the elbows. It should feel secure, not tight.',
      steps: [
        { t: 'No club', d: '10 slow turns back and through with your arms hanging naturally. Feel your upper arms stay with your chest.' },
        { t: 'Quarter swings', d: '10 small swings with a short iron. If you feel the band pull or slip, your arms are moving apart—slow down.' },
        { t: 'Half swings', d: '10 hip-height to hip-height swings where your chest and arms finish together.' },
        { t: 'Band off', d: 'Remove the band and make 5 swings trying to recreate the same feeling.' }
      ],
      feel: 'Your arms and chest turning as one unit, with the band staying settled rather than pulling.',
      mistakes: ['Squeezing your arms together so hard you tense up.', 'Going to full speed too soon.', 'Wearing the band on your forearms.'],
      progress: 'When half swings feel natural, extend to three-quarter swings at the same slow pace.'
    },
    'connection-half-swing': {
      title: 'Slow Connected Half-Swing Routine', product: 'band', duration: '10–12 minutes · 3–4 times a week',
      setup: 'Place the Connection Band around both upper arms, just above the elbows. Use a wedge or short iron.',
      steps: [
        { t: 'Turn together', d: '10 slow half swings. Your chest keeps turning through the finish—don’t let it stop while your hands keep going.' },
        { t: 'Hold the finish', d: '5 half swings holding a balanced finish for two seconds, with your chest facing the target.' },
        { t: 'Short pitches', d: 'If comfortable, hit 10 soft 30–50 yard pitch shots wearing the band.' },
        { t: 'Band off', d: 'Remove the band and hit 5 more, recreating the same feeling.' }
      ],
      feel: 'Your chest and arms arriving at the finish at the same time.',
      mistakes: ['Letting the turn stop at impact.', 'Swinging harder to “test” the band.', 'Skipping the band-off reps.'],
      progress: 'Add longer swings only once short pitches feel connected and balanced.'
    },
    combined: {
      title: 'Rhythm + Connection Routine', product: 'both', duration: '12–15 minutes · alternate days',
      setup: 'Practice each feeling separately first, then combine them in your warm-up.',
      steps: [
        { t: 'Day A — Rhythm', d: 'Tempo Swing Stick: 10 swings listening for a consistent click, then 5 with a “one… two” count.' },
        { t: 'Day B — Connection', d: 'Connection Band above the elbows: 10 slow half swings with chest and arms finishing together.' },
        { t: 'Combined warm-up', d: '5 connected half swings with the band, then 5 rhythm swings with the stick, then hit balls.' },
        { t: 'One cue on the course', d: 'Choose rhythm or connection for the round—not both.' }
      ],
      feel: 'One familiar pace, with the arms and body moving together.',
      mistakes: ['Trying to train both feelings in the same swing at first.', 'Rushing to full speed.', 'Carrying two swing thoughts onto the course.'],
      progress: 'After two weeks, start each range session with the combined warm-up.'
    },
    maintenance: {
      title: 'Maintenance Warm-Up', product: 'either', duration: '5 minutes · before every round or range session',
      setup: 'Use whichever tool matches the feeling you most want to keep.',
      steps: [
        { t: 'Rhythm', d: '10 swings with the Tempo Swing Stick, listening for a consistent click.' },
        { t: 'Connection', d: '5 half swings with the Connection Band above your elbows.' },
        { t: 'First club', d: 'A few shots with your opening club using your full routine.' }
      ],
      feel: 'The same swing you practiced, ready for the first tee.',
      mistakes: ['Skipping the warm-up when you’re short on time.', 'Turning a warm-up into a swing rebuild.'],
      progress: 'Keep it short and identical. That’s the point.'
    }
  };

  /* ================= PRODUCT COPY (verified features only) ================= */
  // Source: drgo1f.myshopify.com homepage (theme "Dr.Golf Product Stories v4"), Oct 2026.
  var productCopy = {
    tempo: {
      eyebrow: '01 / Tempo',
      tagline: 'Rehearse a rhythm you can hear.',
      purpose: 'A weighted training stick with an audible cue, so you can rehearse the same pace rep after rep.',
      features: [
        { t: 'A cue you can hear', d: 'The front section extends during the swing and makes an audible click.' },
        { t: 'Resets for your next rep', d: 'After the swing, the mechanism automatically retracts to its starting position.' },
        { t: 'Shaped for your hands', d: 'A molded, non-slip training grip guides consistent hand placement.' },
        { t: 'Weighted, padded tip', d: 'A stainless-steel weight sits beneath the foam-covered tip.' },
        { t: 'Fits in your bag', d: 'Compact enough to practice at home or at the range.' }
      ],
      feedback: 'Sound. You hear the click as the front section extends, which turns rhythm into something you can listen for—not just something you hope you felt.',
      limits: [
        'It doesn’t measure swing speed or tempo—it gives you a cue to practice against.',
        'It won’t fix your swing on its own. Consistent practice does the work.',
        'It’s built for rehearsal swings, not for hitting balls.'
      ]
    },
    band: {
      eyebrow: '02 / Connection',
      tagline: 'Feel your arms move with your body.',
      purpose: 'A band worn around both upper arms, just above the elbows, that gives you a physical cue to keep your arms moving together with your body.',
      features: [
        { t: 'Worn above the elbows', d: 'Sits around both upper arms, where connection is easiest to feel.' },
        { t: 'A cue you can feel', d: 'You notice the band when your arms start working apart.' },
        { t: 'Simple to use', d: 'Slip it on for practice swings and drills—no setup.' },
        { t: 'Practice anywhere', d: 'At home, at the range, or as part of your warm-up.' }
      ],
      feedback: 'Touch. You feel the band when your arms start to separate, so “moving together” becomes something you can feel during the rep.',
      limits: [
        'It won’t automatically cure a slice, a chicken wing or contact problems.',
        'It’s for practice swings and drills, not for every shot you play.',
        'Start slowly—full-speed swings come later.'
      ]
    }
  };

  return {
    diagnoses: diagnoses, fallbacks: fallbacks, combined: combined, starter: starter, maintenance: maintenance,
    strengthNotes: strengthNotes, secondary: secondary, levelWords: levelWords,
    advice: advice, protocols: protocols, productCopy: productCopy
  };
});
